<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: value => ['make', 'model', 'trim', 'color'].includes(value),
  },
  item: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const uiFlags = useMapGetter('wgptCarCatalog/getUIFlags');

const newAlias = reactive({ name: '', locale: '' });

const aliases = computed(() => props.item?.aliases || []);

const localeOptions = computed(() =>
  ['en', 'ar', 'es'].map(value => ({
    value,
    label: t(`WGPT_CAR_LISTINGS.CATALOG.LOCALES.${value}`),
  }))
);

const localeLabel = locale =>
  locale
    ? t(`WGPT_CAR_LISTINGS.CATALOG.LOCALES.${locale}`)
    : t('WGPT_CAR_LISTINGS.CATALOG.LOCALES.any');

const handleAdd = async () => {
  if (!newAlias.name.trim()) return;
  try {
    await store.dispatch('wgptCarCatalog/createAlias', {
      type: props.type,
      entityId: props.item.id,
      name: newAlias.name.trim(),
      locale: newAlias.locale || null,
    });
    useAlert(t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.ADD_SUCCESS'));
    newAlias.name = '';
    newAlias.locale = '';
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.ERROR')
    );
  }
};

const handleDelete = async catalogAlias => {
  try {
    await store.dispatch('wgptCarCatalog/deleteAlias', {
      type: props.type,
      aliasId: catalogAlias.id,
    });
    useAlert(t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.ERROR')
    );
  }
};

const open = () => dialogRef.value?.open();

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.TITLE', {
        name: item?.name || '',
      })
    "
    :description="t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.DESCRIPTION')"
    :show-confirm-button="false"
    :cancel-button-label="t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.DONE')"
    overflow-y-auto
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <span
          v-if="!aliases.length"
          class="py-2 text-sm text-center text-n-slate-10"
        >
          {{ t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.EMPTY') }}
        </span>
        <div
          v-for="catalogAlias in aliases"
          :key="catalogAlias.id"
          class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-n-alpha-1"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="text-sm truncate text-n-slate-12">
              {{ catalogAlias.name }}
            </span>
            <span
              class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ localeLabel(catalogAlias.locale) }}
            </span>
          </div>
          <span
            v-if="!catalogAlias.custom"
            class="text-xs text-n-slate-10 whitespace-nowrap"
          >
            {{ t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.DEFAULT_BADGE') }}
          </span>
          <Button
            v-else
            icon="i-lucide-trash-2"
            variant="ghost"
            color="slate"
            size="xs"
            type="button"
            :is-loading="uiFlags.isSavingAlias"
            @click="handleDelete(catalogAlias)"
          />
        </div>
      </div>
      <div class="flex items-center gap-2">
        <div class="flex-1">
          <Input
            v-model="newAlias.name"
            :placeholder="t('WGPT_CAR_LISTINGS.CATALOG.ALIAS_NAME_PLACEHOLDER')"
            @keydown.enter.prevent="handleAdd"
          />
        </div>
        <div class="w-36">
          <ComboBox
            v-model="newAlias.locale"
            :options="localeOptions"
            :placeholder="t('WGPT_CAR_LISTINGS.CATALOG.LOCALES.any')"
          />
        </div>
        <Button
          :label="t('WGPT_CAR_LISTINGS.CATALOG.ALIASES_DIALOG.ADD')"
          size="sm"
          type="button"
          :is-loading="uiFlags.isSavingAlias"
          :disabled="!newAlias.name.trim()"
          @click="handleAdd"
        />
      </div>
    </div>
  </Dialog>
</template>
